import React, { useState, useCallback, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { ResumeItem } from '../../services/apiService'
import { TailoredResumeResponse, jobApplicationService } from '../../services/jobApplicationService'
import { useMessageBox, MessageBox } from '../MessageBox'
import { downloadTailoredResumeArtifact } from '../../utils/tailoredResumeArtifacts'
import './Step3CustomResume.css'

interface Step3CustomResumeProps {
  jobApplicationId: string
  tailoredResume: TailoredResumeResponse | null
  selectedResumeId: string | null
  selectionType: 'best-fit' | 'saved' | 'custom'
  availableResumes: ResumeItem[]
  onTailoredResumeUpdated: () => void
  onNext: () => void
  onBack: () => void
}

export const Step3CustomResume: React.FC<Step3CustomResumeProps> = ({
  jobApplicationId,
  tailoredResume,
  selectedResumeId,
  selectionType,
  availableResumes,
  onTailoredResumeUpdated,
  onNext,
  onBack,
}) => {
  const { messageBox, showMessageBox, hideMessageBox } = useMessageBox()
  const [resumeContent, setResumeContent] = useState<string>('')
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false)

  useEffect(() => {
    if (tailoredResume?.resumeContent) {
      setResumeContent(tailoredResume.resumeContent)
    } else {
      setResumeContent('')
    }
  }, [tailoredResume])

  const canExport = useMemo(() => resumeContent.trim().length > 0, [resumeContent])

  const handleDownloadPdf = useCallback(async () => {
    if (!canExport) return
    setIsDownloadingPdf(true)
    try {
      await downloadTailoredResumeArtifact(jobApplicationId, 'pdf')
      onTailoredResumeUpdated()
      toast.success('PDF download is ready')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate PDF')
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [canExport, jobApplicationId, onTailoredResumeUpdated])

  const handleDownloadDocx = useCallback(async () => {
    if (!canExport) return
    setIsDownloadingDocx(true)
    try {
      await downloadTailoredResumeArtifact(jobApplicationId, 'docx')
      onTailoredResumeUpdated()
      toast.success('DOCX download is ready')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate DOCX')
    } finally {
      setIsDownloadingDocx(false)
    }
  }, [canExport, jobApplicationId, onTailoredResumeUpdated])

  // Skip this step if custom resume is not selected
  useEffect(() => {
    if (selectionType !== 'custom' && !selectedResumeId?.startsWith('tailored-')) {
      // Auto-advance to next step if not custom
      onNext()
    }
  }, [selectionType, selectedResumeId, onNext])

  const handleSave = useCallback(async () => {
    if (!resumeContent.trim()) {
      showMessageBox({
        type: 'error',
        title: 'Validation Error',
        message: 'Resume content cannot be empty',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      return
    }

    setIsSaving(true)
    try {
      await jobApplicationService.updateTailoredResume(jobApplicationId, resumeContent)
      setIsEditing(false)
      showMessageBox({
        type: 'success',
        title: 'Saved',
        message: 'Resume changes have been saved successfully',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      onTailoredResumeUpdated()
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Save Failed',
        message: error instanceof Error ? error.message : 'Failed to save resume changes. Please try again.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    } finally {
      setIsSaving(false)
    }
  }, [jobApplicationId, resumeContent, onTailoredResumeUpdated, showMessageBox])

  const baseResumeName = tailoredResume?.baseResumeId
    ? availableResumes.find(r => r.id === tailoredResume.baseResumeId)?.originalFilename || null
    : null

  // If no tailored resume exists and custom is selected, show message
  if (!tailoredResume && selectionType === 'custom' && !selectedResumeId?.startsWith('tailored-')) {
    return (
      <div className="step3-custom-resume">
        <div className="step-header">
          <h3>Custom Resume</h3>
          <p className="step-description">
            No custom tailored resume has been generated yet for this application.
          </p>
        </div>
        <div className="step3-no-resume">
          <p>To generate a custom tailored resume, please use the "Generate Custom Resume" button on the applications page.</p>
        </div>
        <div className="step-actions">
          <button className="btn btn-secondary" onClick={onBack}>
            ← Back
          </button>
          <button className="btn btn-primary" onClick={onNext}>
            Continue →
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="step3-custom-resume">
      <div className="step-header">
        <h3>Customize Resume</h3>
        <p className="step-description">
          Review and edit your custom tailored resume. Make any final adjustments before confirming your selection.
        </p>
      </div>

      {tailoredResume && (
        <>
          {/* Resume Info */}
          <div className="step3-resume-info">
            {baseResumeName && (
              <div className="step3-info-item">
                <span className="step3-info-label">Base Resume:</span>
                <span className="step3-info-value">{baseResumeName}</span>
              </div>
            )}
            {tailoredResume.wizardPreferences && (
              <div className="step3-info-item">
                <span className="step3-info-label">Generated with:</span>
                <span className="step3-info-value">
                  {tailoredResume.wizardPreferences.tones
                    .map(t => t.charAt(0).toUpperCase() + t.slice(1))
                    .join(' + ')} tone, {tailoredResume.wizardPreferences.length} length
                </span>
              </div>
            )}
            {tailoredResume.pipelineData?.generation_profile?.quality_tier && (
              <div className="step3-info-item">
                <span className="step3-info-label">Quality:</span>
                <span className="step3-info-value">
                  {tailoredResume.pipelineData.generation_profile.quality_tier === 'premium'
                    ? 'Premium'
                    : 'Standard'}
                </span>
              </div>
            )}
            <div className="step3-info-item">
              <span className="step3-info-label">Status:</span>
              <span className={`step3-info-value step3-status-${tailoredResume.status}`}>
                {tailoredResume.status === 'generated' && 'Generated'}
                {tailoredResume.status === 'editing' && 'Editing'}
                {tailoredResume.status === 'wizard_in_progress' && 'Wizard In Progress'}
                {tailoredResume.status === 'not_started' && 'Not Started'}
              </span>
            </div>
          </div>

          {/* Resume Content */}
          <div className="step3-resume-content-container">
            <div className="step3-resume-actions">
              <div className="step3-export-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleDownloadPdf}
                  disabled={!canExport || isDownloadingPdf}
                >
                  {isDownloadingPdf ? 'Preparing PDF...' : 'Download PDF'}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleDownloadDocx}
                  disabled={!canExport || isDownloadingDocx}
                >
                  {isDownloadingDocx ? 'Preparing DOCX...' : 'Download DOCX'}
                </button>
              </div>
              {!isEditing ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Resume
                </button>
              ) : (
                <div className="step3-edit-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsEditing(false)
                      setResumeContent(tailoredResume.resumeContent)
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
            {canExport && (
              <div className="step3-export-note">
                <strong>Export tip:</strong> Use Download PDF or Download DOCX for submissions. Both exports preserve real text and use the latest stored tailored resume content.
              </div>
            )}
            {isEditing ? (
              <textarea
                className="step3-resume-editor"
                value={resumeContent}
                onChange={(e) => setResumeContent(e.target.value)}
                placeholder="Enter your resume content here..."
                rows={20}
              />
            ) : (
              <div className="step3-resume-display">
                <pre className="step3-resume-text">{resumeContent}</pre>
              </div>
            )}
          </div>
        </>
      )}

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={onBack}>
          ← Back
        </button>
        <button className="btn btn-primary" onClick={onNext}>
          Continue to Confirmation →
        </button>
      </div>

      {messageBox && <MessageBox {...messageBox} onClose={hideMessageBox} />}
    </div>
  )
}
